import * as actionTypes from "./actionTypes";

const initialPagination = {
  total_records: 0,
  current_page: 1,
  limit: 15,
  pre_page: 1,
  next_page: null,
  left_page: 0,
};

const initialState = {
  fetchIntegrationDashboardLoading: true,
  integrationDashboardData: [],
  statusDistribution: {
    totalUsers: 0,
  },
  remarkCounts: {},
  scenarios: [],
  actionableItems: {},
  pagination: initialPagination,
  integrationDashboardErrorMessage: "",
  downloadIntegrationDashboardLoading: false,
  fetchSummaryLoading: false,
  summaryErrorMessage: "",
};

export default (state = initialState, action = {}) => {
  switch (action.type) {
    case actionTypes.FETCH_INTEGRATION_DASHBOARD:
      return {
        ...state,
        fetchIntegrationDashboardLoading: true,
        integrationDashboardErrorMessage: "",
      };

    case actionTypes.FETCH_INTEGRATION_DASHBOARD_SUCCESS: {
      // Handle nested pagination structure from API
      const apiPagination =
        action.payload.optional?.pagination || action.payload.pagination || {};
      return {
        ...state,
        fetchIntegrationDashboardLoading: false,
        integrationDashboardData: action.payload.data || [],
        pagination: {
          total_records: apiPagination.total_records || 0,
          current_page: apiPagination.current_page || 1,
          limit: apiPagination.limit || 15,
          pre_page: apiPagination.pre_page || 1,
          next_page: apiPagination.next_page || null,
          left_page: apiPagination.left_page || 0,
        },
      };
    }

    case actionTypes.FETCH_INTEGRATION_DASHBOARD_FAILED:
      return {
        ...state,
        fetchIntegrationDashboardLoading: false,
        integrationDashboardData: [],
        pagination: initialPagination,
        integrationDashboardErrorMessage: action.payload,
      };
    case actionTypes.DOWNLOAD_INTEGRATION_DASHBOARD:
      return {
        ...state,
        downloadIntegrationDashboardLoading: true,
        integrationDashboardErrorMessage: "",
      };

    case actionTypes.DOWNLOAD_INTEGRATION_DASHBOARD_SUCCESS:
      return {
        ...state,
        downloadIntegrationDashboardLoading: false,
      };

    case actionTypes.DOWNLOAD_INTEGRATION_DASHBOARD_FAILED:
      return {
        ...state,
        downloadIntegrationDashboardLoading: false,
        integrationDashboardErrorMessage: action.payload,
      };

    case actionTypes.FETCH_INTEGRATION_DASHBOARD_SUMMARY:
      return {
        ...state,
        fetchSummaryLoading: true,
        summaryErrorMessage: "",
      };

    case actionTypes.FETCH_INTEGRATION_DASHBOARD_SUMMARY_SUCCESS: {
      // API response structure: { data: { remark_counts, total_count, actionable_items }, optional: null }
      const responseData = action.payload.data || action.payload;
      const remarkCounts = responseData.remark_counts || {};
      const actionableItems = responseData.actionable_items || {};

      const statusDistribution = {
        totalUsers: responseData.total_count || 0,
        // Store all remark counts dynamically
        ...remarkCounts,
      };

      // Generate scenarios from actionable items dynamically
      // Show all negative remarks (exclude GREEN and RED)
      const negativeRemarks = [
        "RED",
        "OVERWORKED",
        "ABSENT",
        "WORKDAY_DATA_MISSING",
        "DATA_MISSING",
        "EMPLOYEE_ID_MISSING",
      ];
      const scenarios = [];

      // Description and suggestion mapping for different remark types
      const remarkDescriptions = {
        OVERWORKED: {
          description: "Employees found with Active / Workday hrs > 120%",
          suggestion: "Indicates Overworking, provide Overtime Pay.",
        },
        OOO: {
          description:
            "Employee has leave applied (Holiday) but has Active time > 30 min",
          suggestion:
            "Indicates Overworking during leave, provide Overtime Pay.",
        },
        ABSENT: {
          description: "Employees found with Active time = 0",
          suggestion: "Review employee attendance data.",
        },
        WORKDAY_DATA_MISSING: {
          description: "No Workday data found for the employee",
          suggestion: "Review and update employee data.",
        },
        DATA_MISSING: {
          description: "Employees with missing data in both workday and we360",
          suggestion: "Review and update employee data.",
        },
        EMPLOYEE_ID_MISSING: {
          description: "Employees with missing employee id in We360",
          suggestion: "Review and update employee data.",
        },
        WE360_DATA_MISSING: {
          description: "Employees with missing We360 data",
          suggestion: "Check We360 integration status.",
        },
        NA: {
          description: "Employees with N/A status",
          suggestion: "Review employee data and status.",
        },
      };

      // Loop through all actionable items and create scenarios for negative remarks
      Object.keys(actionableItems).forEach((remarkKey) => {
        // Skip GREEN and RED (positive remarks)
        if (!negativeRemarks.includes(remarkKey)) {
          return;
        }

        const item = actionableItems[remarkKey];
        if (item && item.count > 0) {
          const remarkInfo = remarkDescriptions[remarkKey] || {
            description: `Employees with ${remarkKey.replace(
              /_/g,
              " "
            )} status`,
            suggestion: "Review employee data and take appropriate action.",
          };

          scenarios.push({
            remarkKey: remarkKey,
            count: item.count,
            description: remarkInfo.description,
            suggestion: remarkInfo.suggestion,
          });
        }
      });

      return {
        ...state,
        fetchSummaryLoading: false,
        statusDistribution,
        remarkCounts: remarkCounts,
        actionableItems,
        scenarios: [...scenarios],
      };
    }

    case actionTypes.FETCH_INTEGRATION_DASHBOARD_SUMMARY_FAILED:
      return {
        ...state,
        fetchSummaryLoading: false,
        summaryErrorMessage: action.payload,
        statusDistribution: initialState.statusDistribution,
        remarkCounts: {},
        actionableItems: {},
        scenarios: [],
      };

    default:
      return state;
  }
};
